import { useNavigate } from "react-router-dom";
import {
  CalendarClock,
  CreditCard,
  Server,
  Globe,
  CheckCircle2,
  AlertCircle,
  Loader2,
} from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { EmptyState } from "@/components/ui/empty-state";
import { useUserDashboard } from "@/hooks/dashboard/use-user-dashboard";
import { UptimePanel } from "@/components/analytics/uptime-panel";
import { PaymentCountdown } from "./payment-countdown";

function formatCurrency(amount?: number) {
  if (amount === undefined) return "—";
  return `$${amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function UserDashboard() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useUserDashboard();

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-9 w-48" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Skeleton className="h-52 w-full" />
          <Skeleton className="h-52 w-full" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>Failed to load your dashboard.</AlertDescription>
      </Alert>
    );
  }

  const subscription = data?.subscription ?? null;
  const deployment = data?.deployment ?? null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Your subscription and deployment at a glance
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Next payment */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <CalendarClock className="h-4 w-4 text-blue-500" />
              Next payment
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {subscription ? (
              <>
                <PaymentCountdown target={subscription.nextPaymentAt} />
                <div className="text-sm text-slate-500 dark:text-slate-400 space-y-1">
                  <p>
                    Due on{" "}
                    <span className="font-medium text-slate-700 dark:text-slate-200">
                      {format(new Date(subscription.nextPaymentAt), "PPP")}
                    </span>
                  </p>
                  {subscription.plan && (
                    <p>
                      {subscription.plan.displayName} ·{" "}
                      {formatCurrency(subscription.plan.priceInCents)}
                    </p>
                  )}
                </div>
                <Button onClick={() => navigate("/billing")} className="w-full">
                  <CreditCard className="h-4 w-4 mr-2" />
                  Manage billing
                </Button>
              </>
            ) : (
              <EmptyState
                icon={CreditCard}
                title="No active subscription"
                description="Subscribe to a plan to get started."
              />
            )}
          </CardContent>
        </Card>

        {/* Deployment status */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Server className="h-4 w-4 text-violet-500" />
              Deployment status
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!deployment ? (
              <EmptyState
                icon={Loader2}
                title="No deployment yet"
                description="Your deployment will appear here once it is provisioned."
              />
            ) : deployment.provisioningStatus !== "DEPLOYED" ? (
              <div className="flex flex-col items-center gap-3 py-4 text-center">
                <Badge
                  variant="outline"
                  className="bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300"
                >
                  {deployment.provisioningStatus === "PENDING_DEPLOYMENT"
                    ? "Pending deployment"
                    : deployment.provisioningStatus}
                </Badge>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Your deployment is being set up. This page will update once it
                  is live.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {deployment.liveStatus === "UP" ? (
                      <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                    ) : (
                      <AlertCircle className="h-5 w-5 text-red-500" />
                    )}
                    <Badge
                      variant="outline"
                      className={
                        deployment.liveStatus === "UP"
                          ? "bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300"
                          : "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/40 dark:text-red-300"
                      }
                    >
                      {deployment.liveStatus}
                    </Badge>
                  </div>
                  <span className="text-xs text-slate-400">
                    {deployment.lastHeartbeatAt
                      ? `Last seen ${formatDistanceToNow(
                          new Date(deployment.lastHeartbeatAt),
                          { addSuffix: true },
                        )}`
                      : "No heartbeat yet"}
                  </span>
                </div>
                <a
                  href={`https://${deployment.frontendDomain}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                >
                  <Globe className="h-4 w-4" />
                  {deployment.frontendDomain}
                </a>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Uptime timeline (only when there is a live-capable deployment) */}
      {subscription && deployment && deployment.provisioningStatus === "DEPLOYED" && (
        <Card>
          <CardContent className="pt-6">
            <UptimePanel subscriptionId={subscription.id} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
